// Współdzielone prymitywy wizualne dla modali feedbacku pewniaków.
// Wzorzec: SurefireSuccessPopup (paleta granat #233855 + gold #F8AB3B).
// Wszystkie elementy respektują reduced-motion przez prop `reduced`.
import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheckCircle, FiClock, FiAlertTriangle } from 'react-icons/fi';
import orzelKsiazkaIcon from '../../img/icons/orzel_ksiazka.png';

// --- WARIANTY WSPÓLNE ---
export const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0 }
};

export const modalVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 10 },
    visible: {
        scale: 1, opacity: 1, y: 0,
        transition: { type: "spring", damping: 25, stiffness: 300 }
    },
    exit: { scale: 0.95, opacity: 0, transition: { duration: 0.2 } }
};

// --- KONFIG PER FAZA (jedno źródło prawdy) ---
const UNIT_COPY = {
    surefire: {
        repetitionSubtitle: 'Pewniak wrócił do pełnej gotowości. Następna powtórka pojawi się automatycznie.',
        phase1Title: 'Faza 1 Pewniaka zaliczona!',
        phase1Subtitle: 'Twój mózg właśnie zaczął utrwalać pewniaka maturalnego.',
        masteredTitle: 'Pewniak opanowany!',
    },
    niepewniak: {
        repetitionSubtitle: 'Niepewniak wrócił do pełnej gotowości. Następna powtórka pojawi się automatycznie.',
        phase1Title: 'Faza nauki niepewniaka zaliczona!',
        phase1Subtitle: 'Twój mózg właśnie zaczął utrwalać ten typ zadania.',
        masteredTitle: 'Niepewniak opanowany!',
    },
};

export const getPhaseConfig = (progress, message, unit = 'surefire', learningTarget = 3) => {
    const copy = UNIT_COPY[unit] || UNIT_COPY.surefire;
    const isRepetition = message?.includes("Powtórka");

    if (isRepetition) {
        return {
            type: 'repetition',
            title: 'Wiedza odświeżona!',
            subtitle: copy.repetitionSubtitle,
            showTimerInfo: false,
        };
    }

    if (progress <= learningTarget) {
        return {
            type: 'phase1',
            title: copy.phase1Title,
            subtitle: copy.phase1Subtitle,
            showTimerInfo: true,
        };
    }

    return {
        type: 'mastered',
        title: copy.masteredTitle,
        subtitle: 'Ten temat jest teraz w Twojej pamięci długotrwałej. Na maturze zrobisz go na automacie.',
        showTimerInfo: false,
    };
};

// --- BURST CONFETTI ---
export const CelebrationBurst = ({ mini = false, reduced = false }) => {
    if (reduced) return null;
    const count = mini ? 6 : 12;
    const sparkles = Array.from({ length: count }, (_, i) => {
        const angle = (i * 360) / count;
        const rad = (angle * Math.PI) / 180;
        const dist = mini ? (28 + (i % 2) * 12) : (44 + (i % 3) * 18);
        const size = mini ? 4 : (i % 3 === 0 ? 7 : i % 3 === 1 ? 5 : 4);
        return {
            x: Math.round(Math.cos(rad) * dist),
            y: Math.round(Math.sin(rad) * dist),
            delay: 0.25 + i * (mini ? 0.05 : 0.03),
            size,
            color: i % 2 === 0 ? '#F8AB3B' : '#E09F2E',
        };
    });

    return (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
            {sparkles.map(({ x, y, delay, size, color }, i) => (
                <motion.div
                    key={i}
                    initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
                    animate={{ x, y, scale: [0, 1.2, 0], opacity: [0, mini ? 0.7 : 1, 0] }}
                    transition={{ delay, duration: mini ? 0.6 : 0.75, ease: "easeOut", times: [0, 0.35, 1] }}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: size,
                        height: size,
                        marginTop: -(size / 2),
                        marginLeft: -(size / 2),
                        borderRadius: '50%',
                        backgroundColor: color,
                    }}
                />
            ))}
        </div>
    );
};

// --- ORZEŁ + GLOW ---
export const SurefireEagle = ({ type, reduced = false }) => {
    const isMastered = type === 'mastered';
    const isPhase1 = type === 'phase1';
    return (
        <div className="flex justify-center mb-1 relative z-10">
            {(isMastered || isPhase1) && <CelebrationBurst mini={isPhase1} reduced={reduced} />}
            {isMastered && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <motion.div
                        initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.15, duration: 0.6 }}
                        className="w-36 h-36 rounded-full bg-[#F8AB3B]/20 blur-2xl"
                    />
                </div>
            )}
            <motion.img
                initial={reduced ? false : { scale: 0.8, opacity: 0, rotate: -10 }}
                animate={{ scale: isMastered ? 1.15 : 1, opacity: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 180, damping: 12 }}
                src={orzelKsiazkaIcon}
                alt="Sukces"
                className={`object-contain drop-shadow-xl relative z-10 ${isMastered ? 'h-36' : 'h-28'}`}
            />
        </div>
    );
};

// --- ANIMOWANY TRACKER FAZ ---
// Progi domyślne = pewniaki (3 + 2). Niepewniaki mają je per-jednostka
// (prog_opanowania dzielone na nauka/utrwalanie), więc przychodzą z backendu.
export const AnimatedProgressTrack = ({
    progress, phaseType, reduced = false,
    learningTarget = 3, consolidationTarget = 2,
}) => {
    const isPhase1 = phaseType === 'phase1';
    const justCompletedIndex = progress - 1;

    const learningDots = Array.from({ length: learningTarget }, (_, i) => i);
    const consolidationDots = Array.from({ length: consolidationTarget }, (_, i) => learningTarget + i);
    // Przy dłuższych ścieżkach (np. prog_opanowania 8+) kropki muszą zejść z rozmiaru,
    // żeby rząd nie wyszedł poza modal.
    const totalDots = learningTarget + consolidationTarget;
    const dotSize = totalDots > 6 ? "w-5 h-5" : "w-7 h-7";
    const iconSize = totalDots > 6 ? 10 : 13;

    const getDotStyle = (index) => {
        const isDone = progress > index;
        if (isDone) return "bg-[#233855] border-[#233855]";
        if (isPhase1 && index >= learningTarget) return "bg-white border-dashed border-gray-300";
        return "bg-white border border-gray-200";
    };

    const Dot = ({ index }) => {
        const isDone = progress > index;
        const animateNow = justCompletedIndex === index && !reduced;
        return (
            <div className="relative">
                <motion.div
                    initial={animateNow ? { scale: 0.8, opacity: 0 } : false}
                    animate={animateNow ? { scale: 1, opacity: 1 } : {}}
                    transition={{ duration: 0.4, delay: 0.2, type: "spring", stiffness: 200 }}
                    className={`${dotSize} rounded-full flex items-center justify-center border ${getDotStyle(index)}`}
                >
                    {isDone && (
                        <motion.div
                            initial={animateNow ? { scale: 0, rotate: -45 } : false}
                            animate={animateNow ? { scale: 1, rotate: 0 } : {}}
                            transition={{ delay: 0.4, type: "spring" }}
                        >
                            <FiCheckCircle className="text-[#F8AB3B]" size={iconSize} strokeWidth={3} />
                        </motion.div>
                    )}
                </motion.div>
                {animateNow && (
                    <motion.div
                        initial={{ scale: 1, opacity: 0.6 }}
                        animate={{ scale: 2.5, opacity: 0 }}
                        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                        className="absolute inset-0 rounded-full -z-10 bg-[#233855]"
                    />
                )}
            </div>
        );
    };

    return (
        <div className="flex items-center gap-2 justify-center my-4">
            <div className="flex items-center gap-1.5">
                {learningDots.map(i => <Dot key={i} index={i} />)}
            </div>

            <div className="flex items-center px-0.5">
                {progress === learningTarget && isPhase1 ? (
                    <motion.div
                        initial={reduced ? false : { scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.6, type: "spring" }}
                        className="flex items-center gap-1 bg-[#E09F2E]/15 border border-[#E09F2E]/25 rounded-full px-2 py-0.5"
                    >
                        <FiClock className="text-[#E09F2E]" size={9} />
                        <span className="text-[8px] font-bold text-[#E09F2E]">12h</span>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: progress >= learningTarget ? 1 : 0.3 }}
                        className={`w-1.5 h-1.5 rounded-full ${progress >= learningTarget ? 'bg-[#233855]' : 'bg-gray-200'}`}
                    />
                )}
            </div>

            <div className="flex items-center gap-1.5">
                {consolidationDots.map(i => <Dot key={i} index={i} />)}
            </div>
        </div>
    );
};

// --- ANIMACJA RED → GREEN DLA POWTÓRKI ---
export const RepairAnimation = ({ reduced = false }) => (
    <div className="flex items-center justify-center gap-3 my-4">
        <motion.div
            initial={reduced ? false : { scale: 1, opacity: 1 }}
            animate={{ scale: 0.8, opacity: 0.3 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="w-10 h-10 rounded-full bg-[#8A0000]/15 border-2 border-[#8A0000]/30 flex items-center justify-center"
        >
            <FiAlertTriangle className="text-[#8A0000]" size={18} />
        </motion.div>
        <motion.div
            initial={reduced ? false : { scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="flex items-center"
        >
            <div className="w-8 h-0.5 bg-gradient-to-r from-[#8A0000]/30 to-[#233855]/30" />
            <motion.div initial={reduced ? false : { scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.8, type: "spring" }}>
                <FiArrowRight className="text-[#233855] mx-1" size={14} />
            </motion.div>
            <div className="w-8 h-0.5 bg-gradient-to-r from-[#233855]/30 to-[#233855]" />
        </motion.div>
        <motion.div
            initial={reduced ? false : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.9, type: "spring", stiffness: 200 }}
            className="w-10 h-10 rounded-full bg-[#233855] border-2 border-[#233855] flex items-center justify-center shadow-md"
        >
            <FiCheckCircle className="text-[#F8AB3B]" size={18} />
        </motion.div>
    </div>
);

// --- TYTUŁ + OPIS ---
export const SurefireTitle = ({ config, reduced = false }) => {
    const isMastered = config.type === 'mastered';
    return (
        <div className="mb-5 relative z-10">
            <motion.h2
                initial={reduced ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className={`font-bold mb-2 font-apotek tracking-wider uppercase text-[#233855] ${isMastered ? 'text-3xl' : 'text-2xl'}`}
            >
                {config.title}
            </motion.h2>
            <motion.p
                initial={reduced ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45 }}
                className="text-sm text-gray-500 leading-relaxed px-1"
            >
                {config.subtitle}
            </motion.p>
        </div>
    );
};

// --- KARTA „FAZA 2 ZA 12H" (Phase 1) ---
export const SurefirePhase1Card = ({ reduced = false }) => (
    <motion.div
        initial={reduced ? false : { opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 mb-5 text-left"
    >
        <div className="p-2.5 rounded-xl bg-[#233855] shadow-md shrink-0">
            <FiClock className="text-[#F8AB3B]" size={16} />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#233855]">Faza 2 odblokuje się za ~12h</p>
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">Podczas snu mózg pracuje dla Ciebie i utrwala zdobytą wiedzę</p>
        </div>
    </motion.div>
);
